import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook centralizado para acceso por licencia.
 *
 * Uso:
 *   const { licenseType, canAccess, isIndependent, isFranchise } = useLicenseAccess();
 *   if (canAccess('team')) { ... }
 *   if (isFranchise) { showBoth(); }
 *
 * Fuente de verdad: user.allowed_modules + user.license_profile (viene del backend
 * `get_company_license_profile`, afectado por reglas por licencia + overrides admin).
 */
export function useLicenseAccess() {
  const { user } = useAuth();
  const profile = user?.license_profile || {};
  const modules = user?.allowed_modules || profile.included_modules || {};

  const licenseType = String(profile.license_type || user?.business_type || 'agency').toLowerCase();

  const canAccess = (moduleKey) => {
    if (!moduleKey) return false;
    return Boolean(modules[moduleKey]);
  };

  return {
    licenseType,
    businessType: user?.business_type || licenseType,
    modules,
    limits: profile.limits || {},
    monthlyCaps: profile.monthly_caps || {},
    aiTokens: profile.ai_tokens || { included: 0, monthly_limit: 0 },
    planName: profile.plan_name || user?.subscription?.plan || 'trial',
    canAccess,
    isIndependent: licenseType === 'independent',
    isAgency: licenseType === 'agency',
    isDeveloper: licenseType === 'developer',
    isFranchise: licenseType === 'franchise',
    isCarDealer: licenseType === 'car_dealer',
    isOwner: licenseType === 'owner',
    // Conveniencia
    canManageTeam: Boolean(modules.team),
    canManageProperties: Boolean(modules.properties),
    canManageDevelopments: Boolean(modules.developments),
    canManageProducts: Boolean(modules.products),
  };
}

export default useLicenseAccess;
